import { AddAlert, Edit } from '@mui/icons-material';
import { Box, ButtonGroup, Divider, Grid } from '@mui/material';
import { Button } from '~/components/Button';
import type { Reports, Searches } from '~/types';
import type { Filters } from '~/types/filters';
import ActiveFiltersChips from './ActiveFiltersChips';
import SnippetsButton from './SnippetsButton';
import useFeature from '~/hooks/useFeature';
import { useScreenSize } from '~/hooks/useScreenSize';
import ViewOverlapButton from '~/components/ViewOverlapButton/ViewOverlapButton';
import { SearchRatingsButton } from '~/components/SearchRatingsButton';

interface RightColumnHeaderProps {
  uuid: string;
  isEditingSearch: boolean;
  setIsEditingSearch: (value: boolean) => void;
  filters: Filters.Filters;
  onFiltersRemove: (updates: Filters.Filters) => void;
  showSnippets: string | undefined;
  setShowSnippets: (value: string) => void;
  total: number;
  history?: Reports.ReportHistory[];
  isLoadingResults: boolean;
  search: Searches.SearchObject | undefined | null;
  name: string;
}

export default function RightColumnHeader({
  isEditingSearch,
  setIsEditingSearch,
  uuid,
  filters,
  onFiltersRemove,
  showSnippets,
  setShowSnippets,
  total,
  isLoadingResults,
  name,
  search,
}: RightColumnHeaderProps) {
  const [usersAlertsFeatureOn] = useFeature('users_alerts');
  const { lgAndUp } = useScreenSize();
  return (
    <>
      <Grid
        container
        item
        className="right-column-header"
        xs={12}
        px={2}
        display={'flex'}
        justifyContent={'space-between'}>
        {!lgAndUp || (
          <Grid
            container
            item
            xs={12}
            sm={true}
            alignItems={'center'}
            display="flex"
            sx={{
              justifyContent: {
                xs: 'center',
                sm: 'flex-start',
              },
            }}
            className="right-column-header-filter-chips">
            {lgAndUp && !isEditingSearch ? (
              <>
                <Grid item py={2}>
                  <Button
                    onClick={() => setIsEditingSearch(!isEditingSearch)}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<Edit />}>
                    Edit Search
                  </Button>
                </Grid>

                <Divider orientation="vertical" flexItem sx={{ ml: 2 }} />
              </>
            ) : (
              <>
                {search && !isLoadingResults && (
                  <SearchRatingsButton search={search} searches_uuid={uuid} />
                )}
              </>
            )}
            <Grid item py={2} ml={1}>
              <ActiveFiltersChips
                filters={filters}
                isLoading={isLoadingResults}
                onChange={onFiltersRemove}
              />
            </Grid>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          sm={'auto'}
          display="flex"
          sx={{
            flexGrow: 1,
            alignItems: 'center',
            justifyContent: {
              xs: 'center',
              sm: 'flex-end',
            },
            py: 2,
          }}
          className="right-column-header-buttons">
          <ButtonGroup
            size="small"
            variant="outlined"
            disabled={!total || isLoadingResults}
            disableElevation>
            <ViewOverlapButton searches_uuid={uuid} name={name} />

            <SnippetsButton
              showSnippets={showSnippets === '1'}
              setShowSnippets={(enabled) =>
                setShowSnippets(enabled ? '1' : '0')
              }
            />

            {usersAlertsFeatureOn && (
              <Button
                href="/alerts"
                disabled={isLoadingResults || !total}
                className="users-alerts-button"
                startIcon={<AddAlert fontSize="small"></AddAlert>}>
                Create Alerts
              </Button>
            )}
          </ButtonGroup>
        </Grid>
      </Grid>

      <Box sx={{ width: '100%' }}>
        <Divider />
      </Box>
    </>
  );
}
